import type { NLTranslationKey, RuleGroupProcessor, RuleGroupTypeAny } from '../../types';
import { convertFromIC } from '../convertQuery';
import { isRuleGroup, isRuleGroupType, isRuleGroupTypeIC } from '../isRuleGroup';
import { isRuleOrGroupValid } from '../isRuleOrGroupValid';
import { lc } from '../misc';
import { getOption } from '../optGroupUtils';
import { getNLTranslataion } from './utils';

/**
 * Rule group processor used by {@link formatQuery} for "natural_language" format.
 *
 * @group Export
 */
export const defaultRuleGroupProcessorNL: RuleGroupProcessor<string> = (ruleGroup, options) => {
  const {
    fields,
    fallbackExpression,
    getParseNumberBoolean,
    placeholderFieldName,
    placeholderOperatorName,
    placeholderValueName,
    ruleProcessor,
    translations,
    validateRule,
    validationMap,
  } = options;

  const processRuleGroup = (rg: RuleGroupTypeAny, outermostOrLonelyInGroup?: boolean): string => {
    if (
      !isRuleOrGroupValid(
        rg,
        validationMap[
          rg.id ?? /* v8 ignore start -- @preserve */ '' /* v8 ignore stop -- @preserve */
        ]
      )
    ) {
      return outermostOrLonelyInGroup ? fallbackExpression : '';
    }

    const rg2 =
      isRuleGroupTypeIC(rg) && rg.rules.some(r => typeof r === 'string' && lc(r) === 'xor')
        ? convertFromIC(rg)
        : rg;

    const processedRules = [];
    let precedingCombinator = '';
    let firstRule = true;

    for (const rule of rg2.rules) {
      // Independent combinators
      if (typeof rule === 'string') {
        const sep = translations.ruleSeparator ?? ', ';
        precedingCombinator = `${sep}${translations[rule as NLTranslationKey] ?? rule} `;
        continue;
      }

      // Groups
      if (isRuleGroup(rule)) {
        const processedGroup = processRuleGroup(
          rule,
          rg2.rules.length === 1 &&
            !(
              rg2.not ||
              /^xor$/i.test(
                rg2.combinator ??
                  /* v8 ignore start -- @preserve */ '' /* v8 ignore stop -- @preserve */
              )
            )
        );
        // v8 ignore else
        if (processedGroup) {
          if (!firstRule && precedingCombinator) {
            processedRules.push(precedingCombinator);
            precedingCombinator = '';
          }
          firstRule = false;
          processedRules.push(processedGroup);
        }
        continue;
      }

      // Basic rule validation
      const [validationResult, fieldValidator] = validateRule(rule);
      if (
        !isRuleOrGroupValid(rule, validationResult, fieldValidator) ||
        rule.field === placeholderFieldName ||
        rule.operator === placeholderOperatorName ||
        /* v8 ignore next -- @preserve */
        (placeholderValueName !== undefined && rule.value === placeholderValueName)
      ) {
        continue;
      }

      const escapeQuotes = (rule.valueSource ?? 'value') === 'value';

      const fieldData = getOption(fields, rule.field);

      const processedRule = ruleProcessor(rule, {
        ...options,
        parseNumbers: getParseNumberBoolean(fieldData?.inputType),
        escapeQuotes,
        fieldData,
      });

      if (processedRule) {
        if (!firstRule && precedingCombinator) {
          processedRules.push(precedingCombinator);
          precedingCombinator = '';
        }
        firstRule = false;
        processedRules.push(processedRule);
      }
    }

    if (processedRules.length === 0) {
      return fallbackExpression;
    }

    const isXOR = lc(rg2.combinator ?? '') === 'xor';
    const combinator = isXOR ? rg2.combinator!.slice(1) : rg2.combinator;
    const mustWrap = rg2.not || !outermostOrLonelyInGroup || (isXOR && processedRules.length > 1);

    const [prefixTL, suffixTL] = (['groupPrefix', 'groupSuffix'] as const).map(key =>
      rg2.not
        ? isXOR
          ? getNLTranslataion(key, translations, ['not', 'xor'])
          : getNLTranslataion(key, translations, ['not'])
        : isXOR
          ? getNLTranslataion(key, translations, ['xor'])
          : getNLTranslataion(key, translations)
    );

    const prefix = mustWrap ? `${prefixTL} (`.trim() : '';
    const suffix = mustWrap ? `) ${suffixTL}`.trim() : '';

    const sep = translations.ruleSeparator ?? ', ';

    return `${prefix}${processedRules.join(
      isRuleGroupType(rg2)
        ? `${sep}${translations[combinator as NLTranslationKey] ?? combinator} `
        : ''
    )}${suffix}`;
  };

  return processRuleGroup(ruleGroup, true);
};
